import { useEffect, useRef, useState, type PointerEvent } from 'react';
import type { StakeTapResult, StakeTapState } from '../../lib/staking';
import { useHaptic } from '../../hooks/useHaptic';

type Heart = { id: number; x: number; y: number };
let audioContext: AudioContext | null = null;

/** Flush buffered taps to the server this often (and on unmount / hide). */
const FLUSH_MS = 280;
/** Server TAP_MAX_BATCH. */
const MAX_BATCH = 40;
/**
 * Local tap limiter (token bucket). Strictly under the server bucket
 * (20/s, burst 60) so the server never clips our taps and the counter never
 * has to be corrected backwards.
 */
const TAP_BURST = 40;
const TAP_INTERVAL_MS = 1000 / 15;
/** Match server TAP_MAX_PER_SEC — wait this long when a flush was fully rate-clipped. */
const SERVER_TAP_INTERVAL_MS = 1000 / 20;

function getAudioContext() {
  audioContext ??= new AudioContext();
  if (audioContext.state === 'suspended') void audioContext.resume();
  return audioContext;
}

type Props = {
  image: string;
  tap: StakeTapState;
  untilLabel: string;
  onReward: (amount: number) => void;
  onError: (error: unknown) => void;
  onSync: (body: { seq: number; count: number }) => Promise<StakeTapResult>;
};

function tickSound() {
  try {
    const context = getAudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const now = context.currentTime;
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(760, now);
    oscillator.frequency.exponentialRampToValueAtTime(1180, now + 0.07);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.07, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.12);
  } catch {
    // Sound is enhancement-only; WebView audio can be unavailable.
  }
}

function coinSound() {
  try {
    const context = getAudioContext();
    const now = context.currentTime;
    const notes: Array<[number, number, number, number]> = [
      [1318.51, 0, 0.09, 0.16],
      [1975.53, 0.09, 0.26, 0.14],
    ];
    notes.forEach(([frequency, delay, duration, volume]) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(frequency, now + delay);
      gain.gain.setValueAtTime(0.0001, now + delay);
      gain.gain.exponentialRampToValueAtTime(volume, now + delay + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + delay + duration);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(now + delay);
      oscillator.stop(now + delay + duration + 0.03);
    });
  } catch {
    // Sound is enhancement-only; WebView audio can be unavailable.
  }
}

/** Refusals that retrying cannot fix; everything else (network, 5xx) is retried with the same seq. */
function isTerminal(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  if (/liquid_required|limit_exceeded|temporarily_unavailable/.test(message)) return true;
  const status = Number(/^API (\d{3})/.exec(message)?.[1] ?? 0);
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/** Mirror server `applyStakeTapBatch` for display + celebration only. */
function simulateTapProgress(confirmed: StakeTapState, extraTaps: number) {
  let likes = confirmed.likes;
  let likeBonus = confirmed.likeBonus;
  let goal = confirmed.goal;
  let remaining = Math.max(0, Math.floor(extraTaps));
  const crossedBonuses: number[] = [];
  while (remaining > 0) {
    const need = goal - likes;
    if (remaining < need) {
      likes += remaining;
      remaining = 0;
      break;
    }
    remaining -= need;
    likes = 0;
    likeBonus += 1;
    goal *= 2;
    crossedBonuses.push(likeBonus);
  }
  return { likes, likeBonus, goal, rewardUsd: confirmed.rewardUsd, crossedBonuses };
}

function markBonusesThrough(set: Set<number>, likeBonus: number) {
  for (let b = 1; b <= likeBonus; b += 1) set.add(b);
}

export function StakeTapSafe({
  image,
  tap,
  untilLabel,
  onReward,
  onError,
  onSync,
}: Props) {
  const { impact, notify } = useHaptic();
  const [pressed, setPressed] = useState(false);
  const [rewarding, setRewarding] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progressPos, setProgressPos] = useState({ x: 90, y: 75 });
  const [hearts, setHearts] = useState<Heart[]>([]);
  const [displayTap, setDisplayTap] = useState(tap);
  const heartId = useRef(0);
  const progressTimer = useRef<number | null>(null);
  const flushTimer = useRef<number | null>(null);
  const mounted = useRef(true);
  /** Last server-confirmed state (staking credit follows this, not the UI stage). */
  const confirmedTap = useRef(tap);
  /** Taps not yet sent. */
  const pending = useRef(0);
  /** Batch on the wire (or being retried) — keeps the same seq until it lands. */
  const inflightBatch = useRef<{ seq: number; count: number } | null>(null);
  const nextSeq = useRef((tap.lastSeq ?? 0) + 1);
  const budgetAt = useRef(0);
  /** `likeBonus` levels already celebrated — avoids double toast when the server catches up. */
  const celebratedBonuses = useRef<Set<number>>(new Set());

  /** Unsent taps sitting in the local buffer / in-flight batch. */
  const bufferedTaps = () => (inflightBatch.current?.count ?? 0) + pending.current;

  const refresh = () => {
    if (!mounted.current) return;
    const confirmed = confirmedTap.current;
    // Optimistic stage: advance goal / play $1 FX locally; Liquid $ still waits on sync.
    const sim = simulateTapProgress(confirmed, bufferedTaps());
    setDisplayTap({
      likes: sim.likes,
      likeBonus: sim.likeBonus,
      goal: sim.goal,
      rewardUsd: sim.rewardUsd,
      lastSeq: confirmed.lastSeq,
    });
    for (const bonus of sim.crossedBonuses) {
      if (celebratedBonuses.current.has(bonus)) continue;
      celebratedBonuses.current.add(bonus);
      showReward(sim.rewardUsd);
    }
  };

  useEffect(() => {
    // Ignore snapshots older than what a flush already confirmed.
    if ((tap.lastSeq ?? 0) < (confirmedTap.current.lastSeq ?? 0)) return;
    confirmedTap.current = tap;
    nextSeq.current = Math.max(nextSeq.current, (tap.lastSeq ?? 0) + 1);
    markBonusesThrough(celebratedBonuses.current, tap.likeBonus);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tap]);

  useEffect(() => {
    mounted.current = true;
    markBonusesThrough(celebratedBonuses.current, tap.likeBonus);
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      mounted.current = false;
      document.removeEventListener('visibilitychange', onHide);
      if (progressTimer.current) window.clearTimeout(progressTimer.current);
      if (flushTimer.current) window.clearTimeout(flushTimer.current);
      flushTimer.current = null;
      void flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showReward = (amount: number) => {
    if (!mounted.current) return;
    setRewarding(true);
    window.setTimeout(() => {
      coinSound();
      notify('success');
      onReward(amount);
    }, 620);
    window.setTimeout(() => setRewarding(false), 1150);
  };

  const scheduleFlush = (ms: number) => {
    if (flushTimer.current) {
      if (ms > 0) return;
      window.clearTimeout(flushTimer.current);
    }
    flushTimer.current = window.setTimeout(() => {
      flushTimer.current = null;
      void flush();
    }, ms);
  };

  /** Send now when enough taps are buffered to finish the goal (or the batch is full). */
  const nextFlushDelay = () => {
    const confirmed = confirmedTap.current;
    const buffered = bufferedTaps();
    const need = Math.max(0, confirmed.goal - confirmed.likes);
    if ((need > 0 && buffered >= need) || pending.current >= MAX_BATCH) return 0;
    return FLUSH_MS;
  };

  const flush = async () => {
    if (inflightBatch.current || pending.current <= 0) return;
    const batch = { seq: nextSeq.current, count: Math.min(MAX_BATCH, pending.current) };
    pending.current -= batch.count;
    inflightBatch.current = batch;

    for (let attempt = 0; ; attempt += 1) {
      try {
        const result = await onSync(batch);
        const lastSeq = result.tap.lastSeq ?? batch.seq;
        confirmedTap.current = {
          likes: result.tap.likes,
          likeBonus: result.tap.likeBonus,
          goal: result.tap.goal,
          rewardUsd: result.tap.rewardUsd,
          lastSeq,
        };
        nextSeq.current = Math.max(nextSeq.current, lastSeq + 1);
        markBonusesThrough(celebratedBonuses.current, result.tap.likeBonus);
        // Not applied (stale seq from another device, or server budget) — resend, never drop.
        const rejected = Math.max(0, result.tap.claimed - result.tap.accepted);
        pending.current += rejected;
        inflightBatch.current = null;
        // Fallback celebration if optimistic path missed (e.g. catch-up from another device).
        if (mounted.current && result.tap.rewards > 0) {
          const end = result.tap.likeBonus;
          const start = end - result.tap.rewards + 1;
          for (let bonus = start; bonus <= end; bonus += 1) {
            if (celebratedBonuses.current.has(bonus)) continue;
            celebratedBonuses.current.add(bonus);
            showReward(result.tap.rewardUsd);
          }
        }
        refresh();
        if (pending.current > 0) {
          // Budget empty: wait one server tick instead of spinning RTTs for 0 accepts.
          const delay =
            result.tap.accepted === 0 ? SERVER_TAP_INTERVAL_MS : nextFlushDelay();
          scheduleFlush(delay);
        }
        return;
      } catch (error) {
        if (!isTerminal(error) && (mounted.current || attempt < 5)) {
          const sim = simulateTapProgress(confirmedTap.current, bufferedTaps());
          const atGoal = sim.likeBonus > confirmedTap.current.likeBonus;
          await wait(
            atGoal
              ? Math.min(500, 100 * 2 ** attempt)
              : Math.min(3000, 200 * 2 ** attempt),
          );
          continue;
        }
        // Server refused for good (liquid closed, vault full, kill switch): drop
        // unsent taps and roll the optimistic stage back to confirmed truth.
        inflightBatch.current = null;
        pending.current = 0;
        celebratedBonuses.current = new Set();
        markBonusesThrough(celebratedBonuses.current, confirmedTap.current.likeBonus);
        refresh();
        if (mounted.current) onError(error);
        return;
      }
    }
  };

  const handleTap = (event: PointerEvent<HTMLButtonElement>) => {
    setPressed(false);
    requestAnimationFrame(() => setPressed(true));
    window.setTimeout(() => setPressed(false), 130);

    // Faster than the local bucket: no +1 at all, so nothing shown is ever taken back.
    const now = performance.now();
    const base = Math.max(budgetAt.current, now);
    if (base - now > (TAP_BURST - 1) * TAP_INTERVAL_MS) return;
    budgetAt.current = base + TAP_INTERVAL_MS;

    const rect = event.currentTarget.getBoundingClientRect();
    const id = ++heartId.current;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    setHearts((items) => [...items, { id, x, y }]);
    window.setTimeout(() => setHearts((items) => items.filter((item) => item.id !== id)), 1100);
    setProgressPos({ x, y });
    setShowProgress(true);
    if (progressTimer.current) window.clearTimeout(progressTimer.current);
    progressTimer.current = window.setTimeout(() => setShowProgress(false), 600);
    impact('light');
    tickSound();

    pending.current += 1;
    refresh();
    scheduleFlush(nextFlushDelay());
  };

  return (
    <button
      type="button"
      onPointerUp={handleTap}
      className="relative inline-block h-[150px] w-[180px] touch-manipulation border-0 bg-transparent p-0"
      aria-label={untilLabel}
    >
      <img
        src={image}
        alt=""
        draggable={false}
        className={`h-[148px] w-full select-none object-contain transition-transform duration-[120ms] ${
          pressed ? 'translate-y-1 scale-90' : ''
        } ${rewarding ? 'animate-stake-safe-reward' : ''}`}
      />

      {hearts.map((heart) => (
        <span
          key={heart.id}
          className="animate-stake-heart pointer-events-none absolute z-[5] flex -translate-x-1/2 -translate-y-1/2 items-center gap-[3px] whitespace-nowrap text-[15px] font-extrabold text-[#ff3b6b] [text-shadow:0_2px_6px_rgba(255,59,107,.35)]"
          style={{ left: heart.x, top: heart.y }}
        >
          <HeartIcon />
          +1
        </span>
      ))}

      {rewarding && (
        <span className="animate-stake-coin pointer-events-none absolute left-1/2 top-0 z-[6] text-[26px]">
          🪙
        </span>
      )}

      <span
        className={`pointer-events-none absolute z-[6] inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-[20px] bg-white/85 px-3 py-1.5 shadow-[inset_0_0_0_.5px_rgba(0,0,0,.05),0_6px_16px_rgba(20,30,50,.13)] backdrop-blur-xl transition-[opacity,transform] duration-200 ${
          showProgress ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{ left: progressPos.x, top: progressPos.y }}
      >
        <HeartIcon />
        <span className="text-[12.5px] font-bold text-ink">
          {displayTap.likes} / {displayTap.goal}
        </span>
        <span className="h-2 w-[104px] overflow-hidden rounded-[5px] bg-black/[.07] shadow-inner">
          <i
            className="block h-full rounded-[5px] bg-gradient-to-r from-[#ffb36b] via-[#ff5b7f] to-[#ff3b6b] shadow-[0_0_8px_rgba(255,59,107,.5)] transition-[width] duration-300"
            style={{
              width: `${Math.min(100, (displayTap.likes / displayTap.goal) * 100)}%`,
            }}
          />
        </span>
        <span className="text-[11.5px] font-semibold text-muted">{untilLabel}</span>
      </span>
    </button>
  );
}

function HeartIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="#ff3b6b" aria-hidden="true">
      <path d="M12 21s-7.5-4.7-9.4-9A5.2 5.2 0 0 1 12 6.6 5.2 5.2 0 0 1 21.4 12c-1.9 4.3-9.4 9-9.4 9Z" />
    </svg>
  );
}
